import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const RED = "#ef4444";
const AMBER = "#f59e0b";
const ORANGE = "#f58220";
const GREEN = "#22c55e";
const NAVY = "#3b82f6";
const SLATE = "#64748b";

/**
 * Top 5 Faults — bar chart of the most frequent breakdown causes.
 * Buckets by the first non-empty of: `subcategory`, `cause`, `complaint`.
 * Normalises text (lowercase, strip WO#, trim) so "Hydraulic Oil Leak" and
 * "hydraulic oil leak - wo 12345" fold into the same bucket.
 */
export function TopFaultsWidget({ breakdowns }) {
  const data = useMemo(() => rankFaults(breakdowns, 5), [breakdowns]);
  if (!data.length) return <EmptyChart label="No breakdowns in this window." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
        <XAxis type="number" allowDecimals={false} />
        <YAxis dataKey="fault" type="category" width={180} />
        <Tooltip />
        <Bar dataKey="count" fill={RED} radius={[0, 8, 8, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function rankFaults(breakdowns, topN) {
  const bucket = new Map();
  (breakdowns || []).forEach((b) => {
    const raw = b.subcategory || b.cause || b.complaint || "";
    const key = normFault(raw);
    if (!key) return;
    bucket.set(key, (bucket.get(key) || 0) + 1);
  });
  return [...bucket.entries()]
    .map(([fault, count]) => ({ fault: fault.slice(0, 60), count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}
function normFault(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  // Strip trailing WO refs like "- WO 12345" so identical faults fold together.
  return t.replace(/\s*-?\s*(wo|w\/o|work order)\s*#?\s*[\w-]+/i, "").trim();
}

/* ================================================================ */

/**
 * Fleet Health Heatmap — colour-coded grid of every asset with its current
 * status resolved from live data:
 *   • DOWN     — has an open (unapproved) breakdown right now.
 *   • AT RISK  — has an open in-progress inspection (crew is on it).
 *   • WORKING  — has a signed inspection within the past 24 h (recent life).
 *   • READY    — nothing else — clean, available.
 * Assets fetched once on mount from `/assets` (site-scoped by the caller's
 * active site override header). Uses a virtualised-safe grid — max 400
 * chips visible at once — so 500-asset sites don't murder the browser.
 */
export function FleetHealthHeatmap({ inspections, breakdowns }) {
  const [assets, setAssets] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/assets", { params: { limit: 500 } })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.results || []);
        setAssets(list);
      })
      .catch(() => setAssets([]));
    return () => { alive = false; };
  }, []);

  const grouped = useMemo(() => {
    if (!assets) return null;
    const downSet = new Set();
    (breakdowns || []).forEach((b) => {
      const s = String(b.status || "").toLowerCase();
      if (!b.approved_at && s !== "approved" && s !== "closed" && b.asset_id) {
        downSet.add(String(b.asset_id));
      }
    });
    const atRiskSet = new Set();
    const workingSet = new Set();
    const dayMs = 24 * 60 * 60 * 1000;
    const now = Date.now();
    (inspections || []).forEach((i) => {
      const s = String(i.status || "").toLowerCase();
      const aid = String(i.asset_id || "");
      if (!aid) return;
      if (["in_progress", "draft", "pending", "submitted"].some((k) => s.includes(k))) {
        atRiskSet.add(aid);
      }
      const finishedAt = i.approved_at || i.updated_at;
      if (finishedAt && now - new Date(finishedAt).getTime() < dayMs) {
        workingSet.add(aid);
      }
    });
    const rows = { down: [], atRisk: [], working: [], ready: [] };
    assets.forEach((a) => {
      const id = String(a.id || a._id || "");
      if (!id) return;
      if (downSet.has(id)) rows.down.push(a);
      else if (atRiskSet.has(id)) rows.atRisk.push(a);
      else if (workingSet.has(id)) rows.working.push(a);
      else rows.ready.push(a);
    });
    return rows;
  }, [assets, inspections, breakdowns]);

  if (assets === null) {
    return (
      <div className="h-[240px] flex items-center justify-center gap-2 text-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading fleet…
      </div>
    );
  }
  if (!grouped || (assets.length === 0)) {
    return <EmptyChart label="No assets found for this site." />;
  }
  const total = assets.length;
  const legend = [
    { key: "down",    label: "Down",    color: RED,    count: grouped.down.length },
    { key: "atRisk",  label: "At Risk", color: AMBER,  count: grouped.atRisk.length },
    { key: "working", label: "Working", color: NAVY,   count: grouped.working.length },
    { key: "ready",   label: "Ready",   color: GREEN,  count: grouped.ready.length },
  ];
  const rowOrder = ["down", "atRisk", "working", "ready"];
  const colorByRow = { down: RED, atRisk: AMBER, working: NAVY, ready: GREEN };

  return (
    <div data-testid="widget-fleet-health-grid">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {legend.map((l) => (
          <div key={l.key} className="pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
               style={{ background: l.color + "22", border: `1px solid ${l.color}`, color: l.color }}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: l.color }} />
            {l.label} · {l.count}
          </div>
        ))}
        <div className="text-[10px] uppercase tracking-widest text-muted font-black ml-auto">
          Total {total}
        </div>
      </div>
      <div className="space-y-2">
        {rowOrder.map((rk) => (
          grouped[rk].length === 0 ? null : (
            <div key={rk} className="flex flex-wrap gap-1.5">
              {grouped[rk].slice(0, 200).map((a) => (
                <div
                  key={a.id || a._id}
                  className="text-[10px] font-mono-tight font-black uppercase px-1.5 py-1 rounded"
                  style={{
                    background: colorByRow[rk] + "18",
                    border: `1px solid ${colorByRow[rk]}66`,
                    color: colorByRow[rk],
                    minWidth: 56,
                    textAlign: "center",
                  }}
                  title={`${a.id || a._id} · ${a.category || a.asset_type || ""} · ${rk.toUpperCase()}`}
                >
                  {a.id || a._id}
                </div>
              ))}
              {grouped[rk].length > 200 && (
                <div className="text-[10px] text-muted font-black self-center">
                  +{grouped[rk].length - 200} more
                </div>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  );
}

/* ================================================================ */

/**
 * Technician Completion Rate — per-person bar chart showing
 * approved/complete vs still-open inspections. Buckets by
 * `inspector_name` or `assignee_name` (falls back to `created_by_name`).
 * Sorted descending by total volume so the busiest techs top the chart.
 */
export function TechCompletionWidget({ inspections }) {
  const data = useMemo(() => rankTechs(inspections, 8), [inspections]);
  if (!data.length) return <EmptyChart label="No inspection assignments yet." />;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 6" stroke="var(--border)" />
        <XAxis dataKey="name" interval={0} angle={-15} textAnchor="end" height={70} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="done" stackId="a" name="Complete" fill={GREEN} radius={[0, 0, 0, 0]} />
        <Bar dataKey="open" stackId="a" name="Open" fill={ORANGE} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
function rankTechs(inspections, topN) {
  const bucket = new Map();
  (inspections || []).forEach((i) => {
    const name = i.inspector_name || i.assignee_name || i.created_by_name || i.started_by_name;
    if (!name) return;
    if (!bucket.has(name)) bucket.set(name, { name, done: 0, open: 0 });
    const b = bucket.get(name);
    const s = String(i.status || "").toLowerCase();
    if (["approved", "complete", "signed", "closed", "completed"].some((k) => s.includes(k))) b.done += 1;
    else b.open += 1;
  });
  return [...bucket.values()]
    .map((r) => ({ ...r, total: r.done + r.open, rate: r.done + r.open ? Math.round((r.done / (r.done + r.open)) * 100) : 0 }))
    .sort((a, b) => b.total - a.total)
    .slice(0, topN);
}

/* ================================================================ */

/**
 * Cost Estimation — rough parts + labour spend based on PPF trade-hour
 * data. Labour rate is a portal-side default of AUD 145 / hour per trade —
 * override via the `labourRate` prop if the site prefers a different rate.
 * Parts cost is summed straight from `parts[].cost` (or `.total` / `.price`
 * fallbacks). Everything is a *rough estimate* and marked as such in the UI.
 */
export function CostEstimationWidget({ ppfs, labourRate = 145 }) {
  const totals = useMemo(() => calcCost(ppfs, labourRate), [ppfs, labourRate]);
  if (!(ppfs && ppfs.length)) return <EmptyChart label="No PPFs to cost yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <CostTile label="Labour (est.)" value={money(totals.labour)} sub={`${totals.hours.toFixed(1)}h @ $${labourRate}/h`} color={ORANGE} />
      <CostTile label="Parts" value={money(totals.parts)} sub={`${totals.partsCount} line item${totals.partsCount === 1 ? "" : "s"}`} color={NAVY} />
      <CostTile label="Total" value={money(totals.labour + totals.parts)} sub={`${ppfs.length} PPF${ppfs.length === 1 ? "" : "s"}`} color={GREEN} big />
    </div>
  );
}
function calcCost(ppfs, labourRate) {
  let hours = 0; let parts = 0; let partsCount = 0;
  (ppfs || []).forEach((p) => {
    (p.trades_hours || p.trade_hours || []).forEach((th) => {
      const h = Number(th.hours || th.h || 0);
      if (Number.isFinite(h)) hours += h;
    });
    (p.parts || []).forEach((pt) => {
      const c = Number(pt.cost ?? pt.total ?? pt.price ?? 0);
      const qty = Number(pt.qty ?? pt.quantity ?? 1);
      if (Number.isFinite(c) && c > 0) {
        parts += (c * (Number.isFinite(qty) ? qty : 1));
        partsCount += 1;
      } else if (pt.part_number || pt.name) {
        partsCount += 1;
      }
    });
  });
  return { hours, labour: Math.round(hours * labourRate), parts: Math.round(parts), partsCount };
}
function money(n) {
  return "$" + (n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 });
}
function CostTile({ label, value, sub, color, big }) {
  return (
    <div className="panel-flat p-4" style={{ borderColor: color + "55" }}>
      <div className="text-[10px] uppercase tracking-widest font-black text-muted">{label}</div>
      <div className={"font-display font-black tabular-nums leading-none mt-1 " + (big ? "text-4xl md:text-5xl" : "text-3xl md:text-4xl")}
           style={{ color }}>
        {value}
      </div>
      <div className="text-[11px] text-muted mt-1 font-mono-tight">{sub}</div>
    </div>
  );
}

/* ================================================================ */

export function EmptyChart({ label }) {
  return (
    <div className="h-[220px] flex flex-col items-center justify-center gap-2 text-muted text-sm">
      <AlertTriangle className="w-5 h-5" />
      <span>{label || "Not enough data yet."}</span>
    </div>
  );
}

// Placeholder colours re-exported so Insights.jsx can reuse if it wants a
// consistent palette across all cards.
// eslint-disable-next-line no-unused-vars
const _palette = { RED, AMBER, ORANGE, GREEN, NAVY, SLATE };
